import { plainToInstance, Type } from 'class-transformer'
import { ISiteBoxDocumentPdfVersion } from '@core/entities/SiteBoxDocumentPdfVersion'
import { SiteBoxDocumentVersionBase } from '@core/entities/SiteBoxDocumentVersionBase'
import {
  ImageDataPixelScale,
  SiteBoxDocumentImagesData,
} from '@core/entities/SiteBoxDocumentImagesData'
import { SiteboxImageProcessingStatus } from './types'

export class SiteBoxDocumentPdfVersion extends SiteBoxDocumentVersionBase<ISiteBoxDocumentPdfVersion> {
  // Narrowed discriminant: makes `Pdf | NonPdf` a real discriminated union that
  // narrows on `format` (a subclass + its parent would collapse to the parent).
  format: 'PDF'
  imageKey: string
  imageGetUrl: string
  imageDownloadUrl: string
  documentGetUrl: string
  documentDownloadUrl: string
  pdfHeightInPoints: number
  pdfWidthInPoints: number
  imageWidthInPixels: number
  pdfHeight?: number
  pdfWidth?: number

  @Type(() => SiteBoxDocumentImagesData)
  imagesData: SiteBoxDocumentImagesData[]

  imageProcessingStatus: SiteboxImageProcessingStatus

  public static new(payload: ISiteBoxDocumentPdfVersion) {
    const entity = plainToInstance(SiteBoxDocumentPdfVersion, payload)

    return entity
  }

  public getDocumentVersionImageUrl(scale: string): string {
    return this.getScaledImageData(scale)?.getUrl || this?.imageGetUrl
  }

  public getDocumentVersionImageKey(scale: string): string {
    return this.getScaledImageData(scale)?.imageKey || this?.imageKey
  }

  public getDocumentVersionImageTransformData(scale?: ImageDataPixelScale): {
    widthPixelsPerPoint: number
    heightPixelsPerPoint: number
  } {
    if (this.imagesData && scale) {
      const imageData = this.imagesData.find(
        (imageData) => imageData.pixelScale === scale,
      )

      const imageWidthInPixels = imageData?.imageWidthInPixels
      const imageHeightInPixels = imageData?.imageHeightInPixels

      const widthPixelsPerPoint = imageWidthInPixels / this.pdfWidth
      const heightPixelsPerPoint = imageHeightInPixels / this.pdfHeight

      return {
        widthPixelsPerPoint,
        heightPixelsPerPoint,
      }
    } else {
      const heightToWidthRation = this.pdfHeightInPoints / this.pdfWidthInPoints
      const imageHeightInPixels = this.imageWidthInPixels * heightToWidthRation

      const widthPixelsPerPoint =
        this.imageWidthInPixels / this.pdfWidthInPoints
      const heightPixelsPerPoint = imageHeightInPixels / this.pdfHeightInPoints

      return {
        widthPixelsPerPoint,
        heightPixelsPerPoint,
      }
    }
  }

  public getDocumentVersionImageDimensions(scale?: ImageDataPixelScale): {
    width: number
    height: number
  } {
    if (this.imagesData) {
      const imageData = this.imagesData.find(
        (imageData) => imageData.pixelScale === scale,
      )

      return {
        width: imageData?.imageWidthInPixels,
        height: imageData?.imageHeightInPixels,
      }
    }

    const heightToWidthRation = this.pdfHeightInPoints / this.pdfWidthInPoints
    const imageHeightInPixels = this.imageWidthInPixels * heightToWidthRation

    return {
      width: this.imageWidthInPixels,
      height: imageHeightInPixels,
    }
  }

  public getScaledImageData(scale: string): SiteBoxDocumentImagesData {
    return this.imagesData.find((imageData) => imageData.pixelScale === scale)
  }
}
